import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useSnackbar } from 'notistack';
import React, { Fragment, useCallback, useEffect, useState, type JSX } from 'react';
import { api } from '../../apigateway';
import { ERROR_MESSAGE, SUCCESS_MESSAGE_AUTO_HIDE, WARNING_MESSAGE } from '../../components/common/Alert';
import Formular from '../../components/common/Formular';
import { SPACING_BETWEEN_BOXES } from '../../components/common/spacings';
import Bestaetigungsdialog from '../../components/dialog/Bestaetigungsdialog';
import { RechnungspositonenEditierenDialog } from '../../components/dialog/RechnungspositonenEditierenDialog';
import { UmbuchenDialog } from '../../components/dialog/UmbuchenDialog';
import { Dokumententabelle } from '../../components/Dokumententabelle';
import { Rechnungspositionen } from '../../components/Rechnungspositionen';
import { Zahlungseingaenge } from '../../components/Zahlungseingaenge';
import { useVorgangContext } from '../../contexts/vorgangContext';
import { makeGraphqlQuery } from '../../graphql/makeGraphqlQuery';
import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';
import { useUser } from '../../hooks/useUser';
import { DateiKategorie, Dateityp, Status } from '../../shared/constants';
import { getMessageFromError } from '../../shared/throw';
import { rechnungsparameterOperations } from '../../shared/url';
import { aktualisiereStatus } from '../../shared/util';
import { Mandant, Vorgang, VorgangRechnungspositionEintrag, Zahlungseingang } from '../../types';

export function RechnungPage(): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { vorgang, isLoading, setLoading, holeVorgang, aktualisiereVorgang } = useVorgangContext();
  const { isAdmin, isVerwaltung } = useUser();
  const [mandant, setMandant] = useState<Mandant | null>(null);
  const [zahlungseingangZumLoeschen, setZahlungseingangZumLoeschen] = useState<Zahlungseingang | null>();
  const [rechnungspositionen, setRechnungspositionen] = useState<VorgangRechnungspositionEintrag[]>([]);
  const [openRechnungsDialog, setOpenRechnungsDialog] = useState(false);
  const [openUmbuchenDialog, setOpenUmbuchenDialog] = useState(false);
  const [zahlungseingangZumUmbuchen, setZahlungseingangZumUmbuchen] = useState<Zahlungseingang | null>(null);

  useEffect(() => {
    const holeMandantDaten = async () => {
      const mandant = await makeGraphqlQuery<Mandant>(queries.holeMandant, {
        mandant: vorgang?.mandant
      });
      setMandant(mandant);
    };

    if (vorgang?.id) {
      holeMandantDaten();
    }
  }, [vorgang]);

  const zahlungseingangLoeschen = useCallback(
    (zeile: Zahlungseingang) => {
      const gefilterteZahlungseingaenge = vorgang.zahlungseingang?.filter((z) => z.id !== zeile.id) ?? [];
      const status = aktualisiereStatus({ ...vorgang, zahlungseingang: gefilterteZahlungseingaenge });

      aktualisiereVorgang({
        id: vorgang.id,
        status,
        zahlungseingang: gefilterteZahlungseingaenge
      });
    },
    [aktualisiereVorgang, vorgang]
  );

  const zahlungsimportErlaubt = isVerwaltung || isAdmin;

  useEffect(() => {
    if (vorgang.id !== '') {
      api
        .request(rechnungsparameterOperations.get(vorgang.id))
        .then(setRechnungspositionen)
        .catch((error) => enqueueSnackbar(`Fehler beim Laden der Rechnungspositionen: ${getMessageFromError(error)}`, ERROR_MESSAGE));
    }
  }, [enqueueSnackbar, vorgang.id]);

  const aktualisiereRechnungspositionen = (rechnungspositionen: VorgangRechnungspositionEintrag[]) => {
    aktualisiereVorgang({ id: vorgang.id, editierteRechnungspositionen: [...(vorgang.editierteRechnungspositionen ?? []), ...rechnungspositionen] });
  };

  const aktualisieregeaenderteRechnungspositionen = (rechnungspositionen: VorgangRechnungspositionEintrag[]) => {
    aktualisiereVorgang({ id: vorgang.id, editierteRechnungspositionen: rechnungspositionen });
  };

  const generiereStorno = (zuRechnungsnummer: string) => {
    if (vorgang.zahlungseingang?.some((zeile) => zeile.rechnungsnummer === zuRechnungsnummer)) {
      enqueueSnackbar(`Ein Zahlungseingang mit der Rechnungsnummer ${zuRechnungsnummer} existiert bereits.`, WARNING_MESSAGE);
      return;
    }
    setLoading(true);
    makeGraphqlQuery(mutations.generiereStornodokument, {
      vorgangId: vorgang.id,
      zuRechnungsnummer
    })
      .then(() => {
        enqueueSnackbar(`Das Stornodokument wurde erfolgreich generiert.`, SUCCESS_MESSAGE_AUTO_HIDE);
      })
      .then(() => holeVorgang())
      .catch((error) => {
        enqueueSnackbar(`Das Stornodokument konnte nicht generiert werden: ${getMessageFromError(error)}`, ERROR_MESSAGE);
      })
      .finally(() => setLoading(false));
  };

  const generiereGutschrift = (gutschriftWert: string, zuRechnungsnummer: string) => {
    setLoading(true);
    makeGraphqlQuery(mutations.generiereGutschriftdokument, {
      vorgangId: vorgang.id,
      gutschriftWert,
      zuRechnungsnummer
    })
      .then(() => {
        enqueueSnackbar(`Das Gutschriftdokument wurde erfolgreich generiert.`, SUCCESS_MESSAGE_AUTO_HIDE);
      })
      .then(() => holeVorgang())
      .catch((error) => {
        enqueueSnackbar(`Das Gutschriftdokument konnte nicht generiert werden: ${getMessageFromError(error)}`, ERROR_MESSAGE);
      })
      .finally(() => setLoading(false));
  };

  const generiereWeitereRechnung = async (rechnungspositionen: VorgangRechnungspositionEintrag[]) => {
    await makeGraphqlQuery(mutations.generiereWeiteresRechnungsdokument, {
      vorgangId: vorgang.id,
      rechnungspositionen: rechnungspositionen
        .filter((rechnungsposition) => !rechnungsposition.geloescht)
        .map((rechnungsposition) => JSON.stringify(rechnungsposition))
    });
    enqueueSnackbar(`Das Rechnungsdokument wurde erfolgreich generiert.`, SUCCESS_MESSAGE_AUTO_HIDE);
  };

  const bucheZahlungseingangUm = (rechnungsnummer: string) => {
    if (zahlungseingangZumUmbuchen) {
      aktualisiereVorgang({
        id: vorgang.id,
        zahlungseingang: vorgang.zahlungseingang?.map((z) => (z.id === zahlungseingangZumUmbuchen.id ? { ...z, rechnungsnummer } : z))
      });
    }
    setZahlungseingangZumUmbuchen(null);
    setOpenUmbuchenDialog(false);
  };

  return (
    <>
      <Bestaetigungsdialog
        title="Eingang löschen"
        text="Möchten Sie den Zahlungseingang wirklich löschen?"
        open={!!zahlungseingangZumLoeschen}
        onClose={() => setZahlungseingangZumLoeschen(null)}
        onDelete={() => {
          zahlungseingangLoeschen(zahlungseingangZumLoeschen!);
          setZahlungseingangZumLoeschen(null);
        }}
      />

      <Grid container spacing={SPACING_BETWEEN_BOXES}>
        <Rechnungspositionen
          titel="Berechnete Rechnungspositionen"
          vorgang={vorgang}
          mandant={mandant}
          isLoading={isLoading}
          rechnungspositionen={rechnungspositionen}
          geaendetePositionen={false}
          aktualisiereRechnungspositionen={(rechnungspositionen) =>
            aktualisiereRechnungspositionen(rechnungspositionen.filter((rp) => rp.editiert ?? rp.geloescht))
          }
        />
        {vorgang.editierteRechnungspositionen && vorgang.editierteRechnungspositionen?.length > 0 && (
          <Rechnungspositionen
            titel="Geänderte Rechnungspositionen"
            vorgang={vorgang}
            mandant={mandant}
            isLoading={isLoading}
            rechnungspositionen={vorgang.editierteRechnungspositionen}
            geaendetePositionen={true}
            aktualisiereRechnungspositionen={aktualisieregeaenderteRechnungspositionen}
          />
        )}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Formular ueberschrift="Rechnungsdokumente">
            <Dokumententabelle
              zahlungseingaenge={vorgang.zahlungseingang}
              spalten={['dateiname', 'uploaddatum', 'kategorien', 'wert']}
              dokumente={nurRechnungsdokumente(vorgang).map((d) => ({ ...d, dateiname: d.dateiname?.replace('.pdf', '') }))}
              dataTestId="rechnungen"
              gutschriftStoroMenu={isAdmin || isVerwaltung}
              generiereStornoDokument={generiereStorno}
              generiereGutschriftDokument={generiereGutschrift}
            />
          </Formular>
        </Grid>
        {vorgang.rechnungen?.map((eintrag) => (
          <Fragment key={eintrag.rechnungsnummer}>
            <Rechnungspositionen
              titel={
                <>
                  <span>In Rechnung gestellte Rechnungspositionen </span>
                  <b>{eintrag.rechnungsnummer}</b>
                </>
              }
              mandant={mandant}
              vorgang={vorgang}
              isLoading={isLoading}
              rechnungspositionen={eintrag.rechnungspositionen}
              geaendetePositionen={true}
              aktualisiereRechnungspositionen={aktualisieregeaenderteRechnungspositionen}
              canEdit={false}
            />
            <Zahlungseingaenge
              vorgang={vorgang}
              isLoading={isLoading}
              zahlungsimportErlaubt={zahlungsimportErlaubt}
              rechnungsnummer={eintrag.rechnungsnummer}
              setOpenUmbuchenDialog={setOpenUmbuchenDialog}
              setZahlungseingangZumUmbuchen={setZahlungseingangZumUmbuchen}
              setZahlungseingangZumLoeschen={setZahlungseingangZumLoeschen}
            />
          </Fragment>
        ))}

        {(isAdmin || isVerwaltung) && (
          <Grid size={{ xs: 12, lg: 12 }}>
            <Formular ueberschrift="weitere Rechnung generieren">
              <Button
                disabled={![Status.MAHNWESEN, Status.ABGESCHLOSSEN].includes(vorgang.status)}
                variant="contained"
                color="primary"
                onClick={() => setOpenRechnungsDialog(true)}
              >
                Weitere Rechnung generieren
              </Button>
            </Formular>
          </Grid>
        )}
        {openRechnungsDialog && (
          <RechnungspositonenEditierenDialog
            open={openRechnungsDialog}
            onClose={() => setOpenRechnungsDialog(false)}
            aktualisiereRechnungspositionen={(rechnungspositionen) => {
              generiereWeitereRechnung(rechnungspositionen);
              setOpenRechnungsDialog(false);
            }}
            isLoading={isLoading}
            rechnungsparameter={[...(vorgang.rechnungspositionen ?? [])]}
            mandant={mandant}
            gutachtenart={vorgang.gutachtenart}
            reihenfolgeAenderbar={false}
            geaendetePositionen={true}
            saveButtonLabel="Rechnung generieren"
          />
        )}
        {openUmbuchenDialog && (
          <UmbuchenDialog
            onClose={() => {
              setOpenUmbuchenDialog(false);
              setZahlungseingangZumUmbuchen(null);
            }}
            open={openUmbuchenDialog}
            rechnungsnummern={vorgang.rechnungsnummern?.filter((r) => r !== null) ?? []}
            onEdit={bucheZahlungseingangUm}
          />
        )}
      </Grid>
    </>
  );
}

function nurRechnungsdokumente(vorgang: Vorgang) {
  return (vorgang?.dateien ?? [])
    .filter((datei) => datei?.typ === Dateityp.DOKUMENT)
    .filter((datei) =>
      datei?.kategorien?.some((it) =>
        [DateiKategorie.RECHNUNG, DateiKategorie.FREMDRECHNUNG, DateiKategorie.GUTSCHRIFT, DateiKategorie.STORNO].includes(it as DateiKategorie)
      )
    );
}
